import * as React from "react";

// Utils
import { cn } from "../../../lib/utils";

const WRAPPER_SIZE = { small: "size-4", large: "size-6" } as const;
const DOT_SIZE = { small: "size-2", large: "size-3" } as const;

/**
 * Props for the Radio component
 */
type RadioProps = {
	/** Whether the radio is selected */
	checked: boolean;
	/** Callback fired when the radio becomes selected */
	onChange: () => void;
	/** The value submitted with the radio's name */
	value?: string;
	/** Groups radios together so only one in the group can be selected */
	name?: string;
	/** Whether the radio is disabled */
	disabled?: boolean;
	/** The size of the radio */
	size?: "small" | "large";
	/** Optional id for the underlying input, e.g. to pair with a `<label htmlFor>` */
	id?: string;
	/** Additional className to apply to the radio wrapper */
	dangerouslySetClassName?: string;
	/** Required for accessibility unless the radio is wrapped in a label */
	"aria-label"?: string;
	/** Test ID for testing purposes */
	"data-testid"?: string;
};

/**
 * A single design-system radio button. It renders no label of its own — wrap it
 * in a `<label>` (or pass `aria-label`) at the call site. For a labelled list of
 * options use the `RadioButtons` group component instead.
 */
const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
	(
		{ checked, onChange, value, name, disabled = false, size = "small", id, dangerouslySetClassName, ...props },
		ref
	) => (
		<span className={cn("relative inline-block shrink-0", WRAPPER_SIZE[size], dangerouslySetClassName)}>
			<input
				ref={ref}
				type="radio"
				className="peer sr-only"
				id={id}
				checked={checked}
				onChange={onChange}
				value={value}
				name={name}
				disabled={disabled}
				aria-label={props["aria-label"]}
				data-testid={props["data-testid"]}
			/>
			<span
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 rounded-full border border-solid border-neutral-3 bg-neutral-0 peer-checked:border-primary-5 peer-checked:bg-primary-5 peer-focus-visible:ring-1 peer-focus-visible:ring-primary-5 peer-disabled:opacity-disabled"
			/>
			<span
				aria-hidden="true"
				className={cn(
					"pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-neutral-0 opacity-0 peer-checked:opacity-100",
					DOT_SIZE[size]
				)}
			/>
		</span>
	)
);

Radio.displayName = "Radio";

export { Radio };
export type { RadioProps };
